import React, { forwardRef, MutableRefObject } from 'react';
import { Modal as NBModal, IModalProps } from 'native-base';
import { useTheme } from '@springbig/shared';

type ModalWrapperProps = IModalProps & {
    isOpen: boolean;
    onClose?: () => void;
    closeable?: boolean;
    children: React.ReactNode;
};

const ModalWrapper = forwardRef(
    ({
        isOpen,
        onClose,
        closeable = false,
        children,
        ...props
    }: ModalWrapperProps, ref: MutableRefObject<HTMLDivElement>) => {

        const {
            modal,
            modal_text,
            radius_md,
            modal_border_color,
            modal_border_size,
        } = useTheme();

        return (
            <NBModal isOpen={isOpen} onClose={onClose}>
                <NBModal.Content
                    width={'clamp(320px, 90vw, 400px)'}
                    bg={modal}
                    color={modal_text}
                    rounded={radius_md || 0}
                    borderWidth={modal_border_size}
                    borderColor={modal_border_color}
                    shadow={5}
                    {...props}
                    ref={ref}
                >
                    { closeable ? <Modal.CloseButton /> : <></> }
                    { children }
                </NBModal.Content>
            </NBModal>
        );
    });

ModalWrapper.displayName = 'ModalWrapper';

const Header: React.FC<{ children: React.ReactNode } & React.ComponentProps<typeof NBModal.Header>> =
    ({ children, ...props }) => {

        const {
            modal,
            modal_text,
        } = useTheme();

        return (
            <NBModal.Header
                bg={modal}
                color={modal_text}
                borderBottomWidth={0}
                {...props}
            >
                { children }
            </NBModal.Header>
        );
    };

const CloseButton: React.FC<React.ComponentProps<typeof NBModal.CloseButton>> = () => {

    const {
        primary,
    } = useTheme();

    return (
        <NBModal.CloseButton
            _icon={{
                color: primary
            }}
            _hover={{
                backgroundColor: 'transparent',
            }}
        />
    );
};

const Body: React.FC<{ children: React.ReactNode } & React.ComponentProps<typeof NBModal.Body>> =
    ({ children, ...props }) => {

        const {
            modal,
            modal_text,
        } = useTheme();

        return (
            <NBModal.Body
                bg={modal}
                py={0}
                color={modal_text}
                {...props}
            >
                { children }
            </NBModal.Body>
        );
    };

const Footer: React.FC<{ children: React.ReactNode } & React.ComponentProps<typeof NBModal.Footer>> =
    ({ children, ...props }) => {

        const {
            modal,
            modal_text,
        } = useTheme();

        return (
            <NBModal.Footer
                bg={modal}
                color={modal_text}
                borderTopWidth={0}
                {...props}
            >
                { children }
            </NBModal.Footer>
        );
    };

const Modal = Object.assign(ModalWrapper, {
    Header,
    CloseButton,
    Body,
    Footer,
});

export default Modal;
